import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Progress } from "../components/ui/progress";
import axios from "axios";
import NotificationCenter from "./NotificationCenter";
import { 
  LayoutDashboard, BarChart3, Users, FileText, AlertTriangle, AlertCircle,
  LogOut, ChevronRight, FileSignature, Receipt,
  PieChart, Calendar, Euro, BookOpen, Menu, Landmark, ShoppingBag, TrendingUp, Scale,
  Upload, Loader2, UserCircle, Stethoscope, CalendarDays, GraduationCap, Lock, Trash2, Car, Send, Zap,
  FolderArchive, Settings, CheckSquare, FileCheck, Clock, Ban, CreditCard, Building2, History, Sparkles, Activity
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Item "BE-TAYE IA" injecté dans chaque module (assistant contextuel)
const IA_ITEM = { path: "/assistant", label: "BE-TAYE IA", icon: Sparkles };

// Module PILOTAGE (Dashboard Dirigeant)
const navItemsPilotage = [
  { path: "/pilotage", label: "Tableau de bord", icon: Zap },
  IA_ITEM,
];

// Module VENTES
const navItemsVentes = [
  { path: "/", label: "Vue d'ensemble", icon: LayoutDashboard },
  { path: "/analyse", label: "Analyse CA", icon: BarChart3 },
  { path: "/clients", label: "Clients", icon: Users },
  { path: "/factures", label: "Factures émises", icon: FileText },
  { path: "/import-ventes", label: "Import en masse", icon: Upload },
  { path: "/historique-ventes", label: "Historique", icon: Calendar },
  { path: "/vigilance", label: "Vigilance", icon: AlertTriangle },
  IA_ITEM,
];

// Module ACHATS
const navItemsAchats = [
  { path: "/achats", label: "Vue d'ensemble", icon: LayoutDashboard },
  { path: "/factures-fournisseurs", label: "Factures Fournisseurs", icon: FileText },
  { path: "/import-fournisseurs", label: "Import en masse", icon: Upload, hasProgress: true },
  { path: "/historique-achats", label: "Historique", icon: Calendar },
  { path: "/fournisseurs", label: "Fournisseurs", icon: Users },
  IA_ITEM,
];

// Module CONTRATS
const navItemsContrats = [
  { path: "/contrats", label: "Vue d'ensemble", icon: LayoutDashboard },
  { path: "/contrats/portefeuille", label: "Portefeuille", icon: PieChart },
  { path: "/contrats/echeances", label: "Échéances", icon: Calendar },
  { path: "/contrats/financier", label: "Détail Financier", icon: Euro },
  { path: "/contrats/evolution", label: "Évolution", icon: Activity },
  { path: "/contrats/annuaire", label: "Contrats", icon: BookOpen },
  { path: "/contrats/parametres", label: "Paramètres", icon: Settings },
  IA_ITEM,
];

// Module RAPPROCHEMENT BANCAIRE (Bridge API)
const navItemsRapprochement = [
  { path: "/rapprochement-v2", label: "Tableau de bord", icon: LayoutDashboard },
  { path: "/rapprochement-v2/transactions", label: "Transactions", icon: CreditCard },
  { path: "/rapprochement-v2/remises", label: "Remises SEPA", icon: FileText },
  { path: "/rapprochement-v2/bank-statements", label: "Relevés de banque", icon: FileText },
  { path: "/rapprochement-v2/settings", label: "Paramètres", icon: Settings },
  IA_ITEM,
];

// Module RH
const navItemsRH = [
  { path: "/rh/dashboard", label: "Tableau de bord RH", icon: BarChart3 },
  { path: "/rh/salaries", label: "Salariés", icon: Users },
  { path: "/rh/fiches-paie", label: "Fiches de paie", icon: Receipt },
  { path: "/rh/variables-paie", label: "Variables de paie", icon: Send },
  { path: "/rh/contrats", label: "Contrats", icon: FileSignature },
  { path: "/rh/corbeille", label: "Corbeille", icon: Trash2 },
  IA_ITEM,
];

// Module Parc Auto (Fleet)
const navItemsFleet = [
  { path: "/fleet/dashboard", label: "Tableau de bord", icon: BarChart3 },
  { path: "/fleet/vehicles", label: "Véhicules", icon: Car },
  { path: "/fleet/contracts", label: "Contrats", icon: FileSignature },
  { path: "/fleet/claims", label: "Sinistres", icon: AlertTriangle },
  { path: "/fleet/trash", label: "Corbeille", icon: Trash2 },
  IA_ITEM,
];

// Module Comptabilité
const navItemsComptabilite = [
  { path: "/comptabilite", label: "Dossiers mensuels", icon: FolderArchive },
  { path: "/comptabilite/parametres", label: "Paramètres", icon: Settings },
  IA_ITEM,
];

// Module Tâches (To-Do)
const navItemsTaches = [
  { path: "/tasks", label: "Toutes les tâches", icon: CheckSquare },
  { path: "/tasks/my-day", label: "Ma journée", icon: LayoutDashboard },
  { path: "/tasks/important", label: "Important", icon: AlertTriangle },
  { path: "/tasks/planned", label: "Planifié", icon: Calendar },
  { path: "/tasks/dashboard", label: "Tableau de bord", icon: BarChart3 },
  IA_ITEM,
];

// Module Dépenses avec Engagements (Contrats récurrents)
const navItemsEngagements = [
  { path: "/engagements", label: "Vue d'ensemble", icon: LayoutDashboard },
  { path: "/engagements/contrats", label: "Tous les contrats", icon: FileCheck },
  { path: "/engagements/import", label: "Importer un contrat", icon: Upload },
  { path: "/engagements/calendrier", label: "Calendrier échéances", icon: Calendar },
  { path: "/engagements/resiliations", label: "Résiliations", icon: Ban },
  { path: "/engagements/parametres", label: "Paramètres", icon: Settings },
  IA_ITEM,
];

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Collapsible Section Component
const NavSection = ({ title, icon: SectionIcon, items, expanded, onToggle, location, onNav, importProgress }) => {
  const isAnyActive = items.some(item => location.pathname === item.path);
  
  return (
    <div className="mb-1">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-2 py-2 rounded-lg transition-all duration-200 group
          ${expanded ? 'bg-slate-100/80' : 'hover:bg-slate-100/50'}
        `}
        data-testid={`section-${title.toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1 rounded-md transition-colors duration-200 ${
            isAnyActive || expanded ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
          }`}>
            <SectionIcon className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className={`text-[12px] font-medium tracking-tight transition-colors truncate ${
            isAnyActive || expanded ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-900'
          }`}>
            {title}
          </span>
        </div>
        <ChevronRight 
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${expanded ? 'rotate-90' : ''}`} 
        />
      </button>
      
      {/* Section Items */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="pl-3 pr-1 mt-1 space-y-0.5">
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            const hasActiveImport = item.hasProgress && importProgress && importProgress.status === 'processing';
            const isDisabled = item.disabled;
            
            return (
              <div key={item.path}>
                <button
                  onClick={() => !isDisabled && onNav(item.path)}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] transition-all duration-200 group/item
                    ${isDisabled 
                      ? 'text-slate-300 cursor-not-allowed' 
                      : isActive 
                        ? 'text-blue-700 font-medium' 
                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100/50'
                    }
                  `}
                  data-testid={`nav-${item.path.replace(/\//g, '') || 'dashboard'}`}
                >
                  {/* Active Dot Indicator */}
                  <div className={`w-1 h-1 rounded-full transition-all duration-200 shrink-0 ${
                    isDisabled ? 'bg-transparent' : isActive ? 'bg-blue-600 scale-100' : hasActiveImport ? 'bg-blue-500 scale-100 animate-pulse' : 'bg-transparent scale-0 group-hover/item:scale-100 group-hover/item:bg-slate-300'
                  }`} />
                  {hasActiveImport ? (
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" strokeWidth={1.5} />
                  ) : isDisabled ? (
                    <Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" strokeWidth={1.5} />
                  ) : (
                    <Icon className={`w-4 h-4 transition-colors shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover/item:text-slate-600'}`} strokeWidth={1.5} />
                  )}
                  <span className="flex-1 text-left truncate">{item.label}</span>
                  {isDisabled && (
                    <span className="text-[9px] text-slate-300 italic shrink-0">Bientôt</span>
                  )}
                  {hasActiveImport && (
                    <Badge className="bg-blue-500 text-white text-[9px] px-1 py-0">
                      {importProgress.percent}%
                    </Badge>
                  )}
                </button>
                {/* Mini progress bar when import is active */}
                {hasActiveImport && (
                  <div className="mx-2 mt-1 mb-2">
                    <Progress value={importProgress.percent} className="h-1" />
                    <p className="text-[9px] text-slate-500 mt-0.5 truncate">
                      {importProgress.currentFile}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Standalone Nav Item
const StandaloneNavItem = ({ path, label, icon: Icon, location, onNav }) => {
  const isActive = location.pathname === path || location.pathname.startsWith(`${path}/`);
  
  return (
    <button
      onClick={() => onNav(path)}
      className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg transition-all duration-200 group mb-1
        ${isActive 
          ? 'bg-blue-50/80 border border-blue-100 shadow-[0_1px_3px_rgba(37,99,235,0.05)]' 
          : 'hover:bg-slate-100/50'
        }
      `}
      data-testid={`nav-${path.replace(/\//g, '')}`}
    >
      <div className={`p-1 rounded-md transition-colors duration-200 ${
        isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
      }`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <span className={`text-[12px] font-medium tracking-tight transition-colors truncate ${
        isActive ? 'text-blue-700' : 'text-slate-600 group-hover:text-slate-900'
      }`}>
        {label}
      </span>
      {isActive && <ChevronRight className="w-4 h-4 text-blue-500 ml-auto shrink-0" />}
    </button>
  );
};

// Sidebar content (shared between mobile and desktop)
const SidebarContent = ({ onNavigate, ventesExpanded, setVentesExpanded, achatsExpanded, setAchatsExpanded, contratsExpanded, setContratsExpanded, rapprochementExpanded, setRapprochementExpanded, rhExpanded, setRhExpanded, fleetExpanded, setFleetExpanded, comptabiliteExpanded, setComptabiliteExpanded, tachesExpanded, setTachesExpanded, engagementsExpanded, setEngagementsExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const isAdmin = user?.role === "admin";
  
  // Permissions - admins ont tous les accès
  const permissions = user?.permissions || [];
  const hasPermission = (module) => isAdmin || permissions.includes(module);
  
  // No automatic polling in sidebar - import progress only shown on ImportFournisseurs page
  // This prevents API spam that was causing deployment failures
  const importProgress = null;

  const handleNav = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-full bg-[#F8FAFC]">
      {/* Logo Header */}
      <div className="flex items-center gap-2 h-16 px-3 border-b border-slate-200/60">
        <div className="relative">
          <img 
            src="/images/logo-betaye.jpg" 
            alt="BE-TAYE" 
            className="w-9 h-9 rounded-lg object-cover shadow-sm"
          />
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#F8FAFC]" />
        </div>
        <div>
          <h1 className="text-lg font-black tracking-tight text-slate-900">BE-TAYE</h1>
          <p className="text-[10px] font-bold text-slate-400 tracking-wide">DATA INTELLIGENCE</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 scrollbar-hide">
        {/* PILOTAGE Section - visible pour tous avec permission ventes ou admin */}
        {hasPermission("ventes") && (
          <NavSection
            title="Pilotage"
            icon={Zap}
            items={navItemsPilotage}
            expanded={true}
            onToggle={() => {}}
            location={location}
            onNav={handleNav}
          />
        )}

        {/* VENTES Section - visible si permission "ventes" */}
        {hasPermission("ventes") && (
          <NavSection
            title="Ventes"
            icon={TrendingUp}
            items={navItemsVentes}
            expanded={ventesExpanded}
            onToggle={() => setVentesExpanded(!ventesExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}

        {/* ACHATS Section - visible si permission "achats" */}
        {hasPermission("achats") && (
          <NavSection
            title="Achats"
            icon={ShoppingBag}
            items={navItemsAchats}
            expanded={achatsExpanded}
            onToggle={() => setAchatsExpanded(!achatsExpanded)}
            location={location}
            onNav={handleNav}
            importProgress={importProgress}
          />
        )}

        {/* CONTRATS Section - visible si permission "contrats" */}
        {hasPermission("contrats") && (
          <NavSection
            title="Contrats"
            icon={FileSignature}
            items={navItemsContrats}
            expanded={contratsExpanded}
            onToggle={() => setContratsExpanded(!contratsExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}

        {/* Divider - visible seulement si au moins un module au-dessus et un en dessous */}
        {(hasPermission("ventes") || hasPermission("achats") || hasPermission("contrats")) && 
         (hasPermission("rapprochement") || hasPermission("droit_societes")) && (
          <div className="h-px bg-slate-200/60 my-3 mx-2" />
        )}

        {/* RAPPROCHEMENT BANCAIRE Section - Bridge API */}
        {hasPermission("rapprochement") && (
          <NavSection
            title="Rapprochement Bancaire"
            icon={Landmark}
            items={navItemsRapprochement}
            expanded={rapprochementExpanded}
            onToggle={() => setRapprochementExpanded(!rapprochementExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}

        {/* DROIT DES SOCIETES - visible si permission "droit_societes" */}
        {hasPermission("droit_societes") && (
          <StandaloneNavItem
            path="/droit-societes"
            label="Droit des sociétés"
            icon={Scale}
            location={location}
            onNav={handleNav}
          />
        )}

        {/* RH Section - visible si permission "rh" */}
        {hasPermission("rh") && (
          <NavSection
            title="Ressources Humaines"
            icon={UserCircle}
            items={navItemsRH}
            expanded={rhExpanded}
            onToggle={() => setRhExpanded(!rhExpanded)}
            location={location}
            onNav={handleNav}
            importProgress={null}
          />
        )}

        {/* Fleet Section - visible si permission "fleet" ou "rh" */}
        {(hasPermission("fleet") || hasPermission("rh")) && (
          <NavSection
            title="Parc Auto"
            icon={Car}
            items={navItemsFleet}
            expanded={fleetExpanded}
            onToggle={() => setFleetExpanded(!fleetExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}
        
        {/* Comptabilité Section */}
        {(hasPermission("comptabilite") || isAdmin) && (
          <NavSection
            title="Comptabilité"
            icon={FolderArchive}
            items={navItemsComptabilite}
            expanded={comptabiliteExpanded}
            onToggle={() => setComptabiliteExpanded(!comptabiliteExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}
        
        {/* Dépenses avec Engagements Section */}
        {(hasPermission("achats") || isAdmin) && (
          <NavSection
            title="Dépenses avec Engagements"
            icon={FileCheck}
            items={navItemsEngagements}
            expanded={engagementsExpanded}
            onToggle={() => setEngagementsExpanded(!engagementsExpanded)}
            location={location}
            onNav={handleNav}
          />
        )}
        
        {/* Tâches Section - visible pour tous */}
        <NavSection
          title="Tâches"
          icon={CheckSquare}
          items={navItemsTaches}
          expanded={tachesExpanded}
          onToggle={() => setTachesExpanded(!tachesExpanded)}
          location={location}
          onNav={handleNav}
        />

        {/* BE-TAYE IA - Assistant intelligent global (aussi accessible depuis chaque module) */}
        <StandaloneNavItem
          path="/assistant"
          label="BE-TAYE IA"
          icon={Sparkles}
          location={location}
          onNav={handleNav}
        />
        
        {/* Admin: Utilisateurs - toujours visible pour les admins */}
        {isAdmin && (
          <StandaloneNavItem
            path="/admin/users"
            label="Utilisateurs"
            icon={Users}
            location={location}
            onNav={handleNav}
          />
        )}
      </div>

      {/* User Footer */}
      <div className="p-2 mt-auto">
        <div className="flex items-center gap-2 p-2 bg-white border border-slate-200/60 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
            <span className="text-xs font-bold text-white">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
              {isAdmin && (
                <Badge className="text-[9px] px-1 py-0 h-3.5 bg-blue-50 text-blue-600 border-blue-100 font-medium">
                  Admin
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <NotificationCenter onNavigateToTask={(taskId) => navigate(`/tasks?task=${taskId}`)} />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors h-7 w-7"
            title="Déconnexion"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Mobile Header
export const MobileHeader = ({ onOpenMenu }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#F8FAFC] border-b border-slate-200/60 z-40 flex items-center px-4 gap-3 shadow-sm">
      <Button
        variant="ghost"
        size="icon"
        onClick={onOpenMenu}
        className="text-slate-600 hover:bg-slate-100"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </Button>
      
      <div className="flex items-center gap-2 flex-1">
        <img 
          src="/images/logo-betaye.jpg" 
          alt="BE-TAYE" 
          className="w-8 h-8 rounded-lg object-cover"
        />
        <span className="font-bold text-slate-900">BE-TAYE</span>
      </div>

      <NotificationCenter onNavigateToTask={(taskId) => navigate(`/tasks?task=${taskId}`)} />
      
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
        <span className="text-xs font-bold text-white">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </span>
      </div>
    </header>
  );
};

export const Sidebar = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Expand states for all collapsible menus
  const [ventesExpanded, setVentesExpanded] = useState(
    location.pathname === '/' || 
    location.pathname.startsWith('/analyse') || 
    location.pathname.startsWith('/clients') || 
    location.pathname.startsWith('/factures') || 
    location.pathname.startsWith('/vigilance')
  );
  const [achatsExpanded, setAchatsExpanded] = useState(
    location.pathname.startsWith('/achats') || 
    location.pathname.startsWith('/factures-fournisseurs') || 
    location.pathname.startsWith('/fournisseurs')
  );
  const [contratsExpanded, setContratsExpanded] = useState(location.pathname.startsWith('/contrats'));
  const [rapprochementExpanded, setRapprochementExpanded] = useState(
    location.pathname.startsWith('/rapprochement')
  );
  const [rhExpanded, setRhExpanded] = useState(
    location.pathname.startsWith('/rh')
  );
  const [fleetExpanded, setFleetExpanded] = useState(
    location.pathname.startsWith('/fleet')
  );
  const [comptabiliteExpanded, setComptabiliteExpanded] = useState(
    location.pathname.startsWith('/comptabilite')
  );
  const [tachesExpanded, setTachesExpanded] = useState(
    location.pathname.startsWith('/tasks')
  );
  const [engagementsExpanded, setEngagementsExpanded] = useState(
    location.pathname.startsWith('/engagements')
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Desktop sidebar
  if (!isMobile) {
    return (
      <aside className="fixed left-0 top-0 h-screen w-[220px] border-r border-slate-200/60 z-50 shadow-sm">
        <SidebarContent 
          ventesExpanded={ventesExpanded}
          setVentesExpanded={setVentesExpanded}
          achatsExpanded={achatsExpanded}
          setAchatsExpanded={setAchatsExpanded}
          contratsExpanded={contratsExpanded}
          setContratsExpanded={setContratsExpanded}
          rapprochementExpanded={rapprochementExpanded}
          setRapprochementExpanded={setRapprochementExpanded}
          rhExpanded={rhExpanded}
          setRhExpanded={setRhExpanded}
          fleetExpanded={fleetExpanded}
          setFleetExpanded={setFleetExpanded}
          comptabiliteExpanded={comptabiliteExpanded}
          setComptabiliteExpanded={setComptabiliteExpanded}
          tachesExpanded={tachesExpanded}
          setTachesExpanded={setTachesExpanded}
          engagementsExpanded={engagementsExpanded}
          setEngagementsExpanded={setEngagementsExpanded}
        />
      </aside>
    );
  }

  // Mobile sidebar (Sheet/Drawer)
  return (
    <>
      <MobileHeader onOpenMenu={() => setMobileOpen(true)} />
      
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[280px] border-r-0">
          <SidebarContent 
            onNavigate={() => setMobileOpen(false)}
            ventesExpanded={ventesExpanded}
            setVentesExpanded={setVentesExpanded}
            achatsExpanded={achatsExpanded}
            setAchatsExpanded={setAchatsExpanded}
            contratsExpanded={contratsExpanded}
            setContratsExpanded={setContratsExpanded}
            rapprochementExpanded={rapprochementExpanded}
            setRapprochementExpanded={setRapprochementExpanded}
            rhExpanded={rhExpanded}
            setRhExpanded={setRhExpanded}
            fleetExpanded={fleetExpanded}
            setFleetExpanded={setFleetExpanded}
            comptabiliteExpanded={comptabiliteExpanded}
            setComptabiliteExpanded={setComptabiliteExpanded}
            tachesExpanded={tachesExpanded}
            setTachesExpanded={setTachesExpanded}
            engagementsExpanded={engagementsExpanded}
            setEngagementsExpanded={setEngagementsExpanded}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Sidebar;
export { useIsMobile };
